import React, {useEffect, useState} from 'react';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import figureConverter from '../../../../tools/figureConverter';
import api from '../../../../api/axios';
import {useDatePickerStore} from '../DatepickerStore';
import Cookies from 'js-cookie';
import {format} from 'date-fns';
import {useNavigate} from 'react-router-dom'; // Für die Navigation
import {useConstructionManagerStore} from '..//store/ConstructionManagerStore'; // Zustands-Store importieren

const itemsPerPage = 8;

const ConstructionManagerChart = ({searchTerm}) => {
    const [barColorUmsatz, setBarColorUmsatz] = useState('#8884d8');
    const [barColorKosten, setBarColorKosten] = useState('#82ca9d');
    const [barColorWithOhC, setBarColorWithOhC] = useState('#fbe870');
    const [currentPage, setCurrentPage] = useState(1);
    const [projectData, setProjectData] = useState([]);
    const startDate = useDatePickerStore((state) => state.startDate);
    const endDate = useDatePickerStore((state) => state.endDate);

    // Zustandsfunktionen aus dem Store
    const setSelectedManager = useConstructionManagerStore((state) => state.setSelectedManager);
    const navigate = useNavigate(); // Für die Navigation verwenden

    useEffect(() => {
        const getCSSBackgroundColor = (className) => {
            const element = document.createElement('div');
            element.className = className;
            document.body.appendChild(element);
            const bgColor = window.getComputedStyle(element).backgroundColor;
            document.body.removeChild(element);
            return bgColor;
        };

        const umsatzColor = getCSSBackgroundColor('bg-color-1');
        const kostenColor = getCSSBackgroundColor('bg-color-4');

        setBarColorUmsatz(umsatzColor);
        setBarColorKosten(kostenColor);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const formattedStartDate = format(startDate, 'yyyy-MM-dd');
                const formattedEndDate = format(endDate, 'yyyy-MM-dd');
                const accessToken = Cookies.get('accessToken');
                if (!accessToken) {
                    throw new Error('Access Token fehlt.');
                }

                const response = await api.get(
                    `/api/v1/analysis/sitemanager?start=${formattedStartDate}&end=${formattedEndDate}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const snOhcResponse = await api.get(
                    `/api/v1/overhead?cs=SN`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const snOhc = snOhcResponse.data.ohc;

                const nbOhcResponse = await api.get(
                    `/api/v1/overhead?cs=NB`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const nbOhc = nbOhcResponse.data.ohc;

                const huOhcResponse = await api.get(
                    `/api/v1/overhead?cs=HU`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );
                const huOhc = huOhcResponse.data.ohc;

                const {entries} = response.data;
                const formattedData = entries.map((entry) => ({
                    Bauleiter: `${entry.siteManager.forename} ${entry.siteManager.surname}`,
                    Umsatz: parseFloat(entry.revenueSnapshot.revenue),
                    Kosten: parseFloat(entry.revenueSnapshot.costs),
                    Marge: parseFloat(entry.revenueSnapshot.yield),
                    Rohertrag2: parseFloat(entry.revenueSnapshot.revenue - (entry.revenueSnapshot.revenue * (entry.siteManager.companySite === "SN" ? snOhc : (entry.siteManager.companySite === "HU" ? huOhc : nbOhc)) / 100) - entry.revenueSnapshot.costs),
                    overheadKosten: parseFloat(entry.revenueSnapshot.revenue * (entry.siteManager.companySite === "SN" ? snOhc : (entry.siteManager.companySite === "HU" ? huOhc : nbOhc)) / 100),
                    id: entry.siteManager.id,
                }));

                setProjectData(formattedData);
            } catch (error) {
                console.error('Fehler beim Abrufen der Daten:', error);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    const filteredProjects = projectData.filter((project) =>
        project.Bauleiter.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedProjects = filteredProjects.sort((a, b) => b.Marge - a.Marge);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedProjects = sortedProjects.slice(startIndex, endIndex);
    const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);

    const CustomTooltip = ({active, payload}) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip bg-color-3 rounded-lg p-2 opacity-90 border border-color-1">
                    <p>{`Bauleiter: ${payload[0].payload.Bauleiter}`}</p>
                    <p>{`Umsatz: ${figureConverter(payload[0].value, false)}`}</p>
                    <p>{`Kosten: ${figureConverter(payload[1].value, false)}`}</p>
                    <p>{`Overheadkosten: ${figureConverter(payload[0].payload.overheadKosten, false)}`}</p>
                    <p>{`Rohertrag 1: ${figureConverter(payload[0].payload.Marge, false)}`}</p>
                    <p>{`Rohertrag 2: ${figureConverter(payload[0].payload.Rohertrag2, false)}`}</p>
                </div>
            );
        }
        return null;
    };

    const CustomCursor = ({x, y, width, height, fill}) => {
        return <rect x={x} y={y} width={width} height={height} fill={fill || 'rgba(0, 0, 0, 1)'}/>;
    };

    const CustomTick = (props) => {
        const {x, y, payload} = props;
        const currentProject = projectData.find((p) => p.Bauleiter === payload.value);

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={-100}
                    y={0}
                    dy={5}
                    textAnchor="end"
                    fill="#666"
                    style={{
                        fontSize: '14px',
                        fontWeight: 'light',
                    }}
                >
                    {payload.value}
                </text>
                <text
                    x={-10}
                    y={0}
                    dy={5}
                    textAnchor="end"
                    fill="#666"
                    style={{
                        fontSize: '14px',
                        fontWeight: 'light',
                    }}
                >
                    {figureConverter(currentProject?.Rohertrag2 || 0, false)}
                </text>
            </g>
        );
    };

    return (
        <div className="w-full h-full flex flex-col justify-center pt-2">
            <ResponsiveContainer width="100%" height={450}>
                <BarChart
                    data={displayedProjects}
                    layout="vertical"
                    margin={{top: 0, right: 0, left: 200, bottom: 0}}
                    onClick={({activePayload}) => {
                        if (activePayload && activePayload.length > 0) {
                            const manager = activePayload[0].payload;
                            setSelectedManager(manager); // Speichere den ausgewählten Bauleiter im Zustand
                            navigate(`/reports/${manager.id}`); // Navigiere zur Detailseite des Bauleiters mit der ID
                        } else {
                            console.warn('activePayload is undefined or empty');
                        }
                    }}
                >
                    <CartesianGrid vertical={false} horizontal={false}/>
                    <YAxis dataKey="Bauleiter" type="category" tick={<CustomTick/>}/>
                    <XAxis
                        type="number"
                        tickFormatter={(tick) =>
                            new Intl.NumberFormat('de-DE', {minimumFractionDigits: 0}).format(tick) + ' €'
                        }
                    />
                    <Tooltip content={<CustomTooltip/>} cursor={<CustomCursor fill="rgba(0, 0, 255, 0.03)"/>}/>
                    <Bar dataKey="Umsatz" fill={barColorUmsatz} barSize={15}/>
                    <Bar dataKey="Kosten" fill={barColorKosten} barSize={15} stackId={"a"}/>
                    <Bar dataKey="overheadKosten" fill={barColorWithOhC} barSize={15} stackId={"a"}/>
                </BarChart>
            </ResponsiveContainer>

            <div className="flex justify-center mt-4">
                {Array.from({length: totalPages}, (_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`ml-2 px-3 py-2 w-8 font-color-1 text-sm flex items-center text-center shadow-sm border border-color-2 rounded-lg font-medium ${
                            currentPage === index + 1 ? '' : 'bg-color-5 font-color-2'
                        }`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default ConstructionManagerChart;
